import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ChatCircle, PaperPlaneTilt, Users, MagnifyingGlass,
  ArrowLeft, Paperclip, Check, Checks, X, Plus,
  Smiley, Image as ImageIcon, File as FileIcon
} from "@phosphor-icons/react";

export default function MessagingPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [search, setSearch] = useState("");
  const [showNewChat, setShowNewChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attachment, setAttachment] = useState(null);
  const [onlineIds, setOnlineIds] = useState([]);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/messaging/conversations");
      setConversations(data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  // Poll online status
  const fetchOnline = useCallback(async () => {
    try {
      const { data } = await api.get("/users/online-status");
      setOnlineIds(data.online_ids || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchOnline(); const i = setInterval(fetchOnline, 15000); return () => clearInterval(i); }, [fetchOnline]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    if (activeConvo) {
      const poll = async () => {
        try {
          const { data } = await api.get(`/messaging/conversations/${activeConvo.id}/messages`);
          setMessages(data.items || []);
        } catch { /* ignore */ }
      };
      pollRef.current = setInterval(poll, 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConvo]);

  const loadContacts = async () => {
    const { data } = await api.get("/messaging/contacts");
    setContacts(data.items || []);
    setShowNewChat(true);
  };

  const openConversation = async (convo) => {
    setActiveConvo(convo);
    try {
      const { data } = await api.get(`/messaging/conversations/${convo.id}/messages`);
      setMessages(data.items || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch { toast.error("Failed to load messages"); }
  };

  const startDirectChat = async (contactId) => {
    try {
      const { data } = await api.post("/messaging/conversations", { recipient_id: contactId, type: "direct" });
      setShowNewChat(false);
      setActiveConvo(data);
      const msgs = await api.get(`/messaging/conversations/${data.id}/messages`);
      setMessages(msgs.data.items || []);
      loadConversations();
    } catch { toast.error("Failed to start conversation"); }
  };

  const handleAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment({
        file_id: data.id,
        filename: data.original_filename,
        content_type: data.content_type,
        size: data.size,
      });
    } catch { toast.error("Failed to upload file"); }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const sendMessage = async () => {
    if ((!newMsg.trim() && !attachment) || !activeConvo) return;
    try {
      const payload = { content: newMsg };
      if (attachment) {
        payload.attachment = {
          file_id: attachment.file_id,
          filename: attachment.filename,
          content_type: attachment.content_type,
          size: attachment.size,
        };
      }
      await api.post(`/messaging/conversations/${activeConvo.id}/messages`, payload);
      setNewMsg("");
      setAttachment(null);
      const { data } = await api.get(`/messaging/conversations/${activeConvo.id}/messages`);
      setMessages(data.items || []);
      loadConversations();
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch { toast.error("Failed to send"); }
  };

  const getConvoName = (convo) => {
    if (convo.name) return convo.name;
    const other = (convo.participant_details || []).find(p => p.id !== user?.id);
    return other?.name || "Conversation";
  };

  const getConvoOtherId = (convo) => {
    if (convo.type !== "direct") return null;
    const other = (convo.participant_details || []).find(p => p.id !== user?.id);
    return other?.id || null;
  };

  const isOnline = (uid) => onlineIds.includes(uid);

  const getConvoInitial = (convo) => {
    if (convo.type === "team") return null;
    return getConvoName(convo).charAt(0).toUpperCase();
  };

  const getReadStatus = (msg) => {
    if (msg.sender_id !== user?.id) return null;
    const otherParticipants = activeConvo?.participants?.filter(p => p !== user?.id) || [];
    const readByOthers = (msg.read_by || []).filter(id => id !== user?.id);
    const deliveredTo = msg.delivered_to || [];
    if (readByOthers.length > 0 && readByOthers.length >= otherParticipants.length) {
      return "read";
    }
    if (deliveredTo.length > 0) {
      return "delivered";
    }
    return "sent";
  };

  const roleLabel = (r) => ({ opportunity_finder: "RP", candidate_finder: "CF", team_leader: "TL", admin: "Admin", senior_recruitment_partner: "SRP" }[r] || r);

  const filteredConvos = conversations.filter(c => {
    if (!search) return true;
    return getConvoName(c).toLowerCase().includes(search.toLowerCase());
  });
  const filteredContacts = contacts.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000 && d.getDate() === now.getDate()) {
      return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    }
    if (diff < 604800000) {
      return d.toLocaleDateString("en-GB", { weekday: "short" });
    }
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="messaging-page" className="flex h-[calc(100vh-80px)] -m-4 md:-m-6 bg-white overflow-hidden">
      {/* Sidebar */}
      <div className={`w-[340px] border-r border-[#E5E7EB] flex flex-col bg-[#FAFAFA] ${activeConvo ? "hidden md:flex" : "flex"}`}>
        {/* Header */}
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-[#0F172A] tracking-tight">Messages</h2>
            <button
              onClick={loadContacts}
              className="w-8 h-8 rounded-full bg-[#0F172A] flex items-center justify-center text-white hover:bg-[#1E293B] transition"
              data-testid="new-chat-btn"
            >
              <Plus size={16} weight="bold" />
            </button>
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm bg-white border-[#E5E7EB] rounded-full"
              data-testid="search-conversations"
            />
          </div>
        </div>

        {/* New chat picker */}
        {showNewChat && (
          <div className="border-b border-[#E5E7EB] max-h-56 overflow-y-auto bg-white mx-3 mb-2 rounded-xl shadow-sm">
            <div className="px-3 py-2 flex items-center justify-between border-b border-[#F3F4F6]">
              <span className="text-xs font-bold text-[#0F172A]">New conversation</span>
              <button onClick={() => setShowNewChat(false)} className="text-[#9CA3AF] hover:text-[#374151]"><X size={14} /></button>
            </div>
            {filteredContacts.map(c => (
              <button
                key={c.id}
                onClick={() => startDirectChat(c.id)}
                className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-[#F8F9FA] transition text-left"
                data-testid={`contact-${c.id}`}
              >
                <div className="relative">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0F172A] to-[#334155] flex items-center justify-center text-white text-xs font-bold shadow-sm">
                    {c.name.charAt(0)}
                  </div>
                  {isOnline(c.id) && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white" data-testid={`online-dot-${c.id}`} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{c.name}</p>
                  <p className="text-[10px] text-[#6B7280]">{isOnline(c.id) ? "Online · " : ""}{roleLabel(c.role)}{c.team_name ? ` · ${c.team_name}` : ""}</p>
                </div>
              </button>
            ))}
            {filteredContacts.length === 0 && <p className="px-3 py-4 text-xs text-[#9CA3AF] text-center">No contacts found</p>}
          </div>
        )}

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto">
          {filteredConvos.length === 0 ? (
            <div className="p-8 text-center">
              <ChatCircle size={36} className="text-[#D1D5DB] mx-auto mb-2" />
              <p className="text-sm text-[#9CA3AF]">No conversations yet</p>
            </div>
          ) : filteredConvos.map(c => {
            const isActive = activeConvo?.id === c.id;
            return (
              <button
                key={c.id}
                onClick={() => openConversation(c)}
                className={`w-full px-4 py-3 flex items-center gap-3 transition text-left border-l-2 ${
                  isActive ? "bg-white border-l-[#C5A059] shadow-sm" : "border-l-transparent hover:bg-white/60"
                }`}
                data-testid={`convo-${c.id}`}
              >
                <div className="relative">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0F172A] to-[#334155] flex items-center justify-center text-white text-sm font-bold shadow-sm">
                    {c.type === "team" ? <Users size={18} /> : getConvoInitial(c)}
                  </div>
                  {c.unread_count > 0 ? (
                    <span className="absolute -top-0.5 -right-0.5 bg-[#EF4444] text-white text-[9px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 shadow-sm">
                      {c.unread_count > 99 ? "99+" : c.unread_count}
                    </span>
                  ) : c.type === "direct" && isOnline(getConvoOtherId(c)) ? (
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className={`text-sm truncate ${c.unread_count > 0 ? "font-bold text-[#0F172A]" : "font-medium text-[#374151]"}`}>{getConvoName(c)}</p>
                    <span className="text-[10px] text-[#9CA3AF] shrink-0 ml-2">{c.last_message_at ? formatTime(c.last_message_at) : ""}</span>
                  </div>
                  <p className={`text-xs truncate ${c.unread_count > 0 ? "text-[#374151] font-medium" : "text-[#9CA3AF]"}`}>
                    {c.last_message_preview || "No messages yet"}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat area */}
      <div className={`flex-1 flex flex-col bg-[#F0F2F5] ${!activeConvo ? "hidden md:flex" : "flex"}`}>
        {!activeConvo ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#E5E7EB] flex items-center justify-center mx-auto mb-4">
                <ChatCircle size={36} className="text-[#9CA3AF]" />
              </div>
              <p className="text-base font-medium text-[#6B7280]">Select a conversation</p>
              <p className="text-xs text-[#9CA3AF] mt-1">Choose from your existing chats or start a new one</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-3 bg-white shadow-sm">
              <button onClick={() => { setActiveConvo(null); loadConversations(); }} className="md:hidden text-[#6B7280] hover:text-[#0F172A]"><ArrowLeft size={20} /></button>
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0F172A] to-[#334155] flex items-center justify-center text-white text-sm font-bold shadow-sm">
                  {activeConvo.type === "team" ? <Users size={16} /> : getConvoInitial(activeConvo)}
                </div>
                {activeConvo.type === "direct" && isOnline(getConvoOtherId(activeConvo)) && (
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-[#0F172A]">{getConvoName(activeConvo)}</p>
                <p className="text-[10px] text-[#6B7280]">
                  {activeConvo.type === "team" ? "Team conversation" : (
                    isOnline(getConvoOtherId(activeConvo)) ? <span className="text-emerald-600 font-medium">Online</span> : "Offline"
                  )}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d1d5db' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}>
              {messages.map((m, idx) => {
                const isMe = m.sender_id === user?.id;
                const readStatus = getReadStatus(m);
                const showSenderName = !isMe && activeConvo.type === "team";
                const prevMsg = messages[idx - 1];
                const sameSender = prevMsg && prevMsg.sender_id === m.sender_id;
                const timeGap = prevMsg ? (new Date(m.created_at) - new Date(prevMsg.created_at)) > 300000 : true;

                return (
                  <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"} ${!sameSender || timeGap ? "mt-3" : "mt-0.5"}`}>
                    <div className={`max-w-[75%] relative group ${isMe ? "order-1" : "order-1"}`}>
                      {/* Message bubble */}
                      <div className={`px-3 py-2 rounded-2xl shadow-sm ${
                        isMe
                          ? "bg-[#0F172A] text-white rounded-br-md"
                          : "bg-white text-[#0F172A] rounded-bl-md"
                      }`}>
                        {showSenderName && !sameSender && (
                          <p className="text-[10px] font-bold text-[#C5A059] mb-0.5">{m.sender_name}</p>
                        )}

                        {/* Attachment */}
                        {m.attachment && (
                          <div
                            className={`mb-1.5 p-2 rounded-lg flex items-center gap-2 cursor-pointer hover:opacity-80 ${isMe ? "bg-white/10" : "bg-[#F1F5F9]"}`}
                            onClick={() => {
                              const fid = m.attachment.file_id;
                              if (fid) {
                                api.get(`/files/download/${fid}`, { responseType: "blob" }).then(resp => {
                                  const url = window.URL.createObjectURL(new Blob([resp.data]));
                                  const a = document.createElement("a");
                                  a.href = url; a.download = m.attachment.filename || "file";
                                  document.body.appendChild(a); a.click(); a.remove();
                                  window.URL.revokeObjectURL(url);
                                }).catch(() => toast.error("Download failed"));
                              }
                            }}
                            data-testid={`msg-attachment-${m.id}`}
                          >
                            <FileIcon size={16} className={isMe ? "text-[#C5A059]" : "text-[#6B7280]"} />
                            <span className="text-xs truncate flex-1">{m.attachment.filename || "Attachment"}</span>
                            {m.attachment.size && <span className={`text-[9px] ${isMe ? "text-white/40" : "text-[#9CA3AF]"}`}>{m.attachment.size < 1024 ? `${m.attachment.size} B` : m.attachment.size < 1048576 ? `${(m.attachment.size / 1024).toFixed(1)} KB` : `${(m.attachment.size / 1048576).toFixed(1)} MB`}</span>}
                          </div>
                        )}

                        <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{m.content}</p>

                        <div className="flex items-center justify-end gap-1 mt-0.5 -mb-0.5">
                          <span className={`text-[10px] ${isMe ? "text-white/50" : "text-[#9CA3AF]"}`}>
                            {new Date(m.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {isMe && readStatus && (
                            <span className="ml-0.5">
                              {readStatus === "sent" && <Check size={12} className="text-white/40" />}
                              {readStatus === "delivered" && <Checks size={12} className="text-white/40" />}
                              {readStatus === "read" && <Checks size={12} className="text-[#53BDEB]" />}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Attachment preview */}
            {attachment && (
              <div className="px-4 py-2 bg-white border-t border-[#E5E7EB] flex items-center gap-2">
                <FileIcon size={16} className="text-[#C5A059]" />
                <span className="text-xs text-[#374151] flex-1 truncate">{attachment.filename}</span>
                <span className="text-[10px] text-[#9CA3AF]">{attachment.size < 1024 ? `${attachment.size} B` : `${(attachment.size / 1024).toFixed(1)} KB`}</span>
                <button onClick={() => setAttachment(null)} className="text-[#9CA3AF] hover:text-[#EF4444]"><X size={14} /></button>
              </div>
            )}

            {/* Input area */}
            <div className="px-4 py-3 bg-white border-t border-[#E5E7EB]">
              <div className="flex items-center gap-2">
                <input type="file" ref={fileInputRef} onChange={handleAttachment} accept=".pdf,.doc,.docx,.txt,.csv,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="msg-file-input" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[#6B7280] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition"
                  data-testid="attach-file-btn"
                >
                  <Paperclip size={18} />
                </button>
                <div className="flex-1 relative">
                  <input
                    value={newMsg}
                    onChange={e => setNewMsg(e.target.value)}
                    placeholder="Type a message..."
                    className="w-full h-10 px-4 rounded-full bg-[#F1F5F9] border-0 text-sm text-[#0F172A] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#C5A059]/30"
                    data-testid="message-input"
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  />
                </div>
                <button
                  onClick={sendMessage}
                  disabled={!newMsg.trim() && !attachment}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition shadow-sm ${
                    newMsg.trim() || attachment
                      ? "bg-[#0F172A] text-white hover:bg-[#1E293B]"
                      : "bg-[#E5E7EB] text-[#9CA3AF]"
                  }`}
                  data-testid="send-msg-btn"
                >
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
